<script setup lang="ts">
import BlockContent from '../UI/Content/BlockContent.vue'
import PlaceholderContent from '../UI/Loading/PlaceholderContent.vue'
import DocumentSignSignersForm from './DocumentSignSignersForm.vue'
import DocumentSignerList from './DocumentSignerList.vue'
import type { DocumentAssoc, DocumentSign, DocumentSignFile } from '../../../../Types/document'
import { computed, nextTick, ref, watch } from 'vue'
import useApi from '../../Composables/useApi'
import GenerateAndAddToSignButton from './GenerateAndAddToSignButton.vue'
import DocumentSignFollowerList from './DocumentSignFollowerList.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import ModalConfirmation from '../UI/Modal/ModalConfirmation.vue'
import useModal from '../../../../Composables/useModal.ts'
import useLoading from '../../../../Composables/useLoading.ts'
import { useDocumentSignApi } from '../../../../Composables/DocumentSign/useDocumentSignApi.ts'
import { DocumentSignStatusEnum } from '../../../../Constants/DocumentSignStatus'

interface Props {
  contractId: number
  documentSignId: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const {
  modal: deleteConfirmationModal,
  open: openConfirmationModal,
  close: closeConfirmationModal,
} = useModal({
  prefix: 'document-sign-delete',
})

const { getJSON } = useApi()

const sending = ref<boolean>(false)
const documentSign = ref<DocumentSign | null>(null)
const documentSignerList = ref<InstanceType<typeof DocumentSignerList> | null>(null)
const generateAndAddToSignButton = ref<InstanceType<typeof GenerateAndAddToSignButton> | null>(null)
const isDeletable = computed(() => documentSign.value && documentSign.value.isCancelable)
const { loading: deletingDocumentSign } = useLoading(false)

const isDisabled = computed<boolean>(() => {
  return documentSign.value?.isSentToProvider || sending.value
})

const isDocumentSignerListDisabled = computed<boolean>(() => {
  return sending.value || documentSign.value?.status === DocumentSignStatusEnum.CANCELLED
})

async function loadDocumentSign(): Promise<void> {
  documentSign.value = null
  try {
    documentSign.value = (await getJSON(`document-signs/${props.documentSignId}`)) as DocumentSign
  } catch {
    documentSign.value = null
  }
}

async function handleSignersUpdated(): Promise<void> {
  await loadDocumentSign()
  await documentSignerList.value?.loadSigners()
  await nextTick()
  await generateAndAddToSignButton.value?.fetchDocumentTypes()
}

function handleSignFileReplaced({
  documentSignFile,
  documentAssoc,
}: {
  documentSignFile: DocumentSignFile
  documentAssoc: DocumentAssoc
}): void {
  if (!documentSign.value) {
    return
  }

  for (const existingDocumentSignFile of documentSign.value.documentSignFiles) {
    if (existingDocumentSignFile.id !== documentSignFile.id) {
      continue
    }

    existingDocumentSignFile.document = documentAssoc.document
    existingDocumentSignFile.documentAssoc = documentAssoc
  }
}

const { deleteDocumentSign } = useDocumentSignApi()
async function deleteSign(): Promise<void> {
  deletingDocumentSign.value = true
  try {
    await deleteDocumentSign(Number(props.documentSignId))

    emit('close')
  } finally {
    deletingDocumentSign.value = false
    closeConfirmationModal()
  }
}

watch(
  () => props.documentSignId,
  async () => {
    await loadDocumentSign()
  },
  { immediate: true },
)
</script>

<template>
  <div class="col-12 d-flex">
    <ButtonAll type="default" margin="no" icon="close" label="main.close" class="mr-auto" @click="emit('close')" />

    <ButtonAll
      v-if="isDeletable"
      type="danger"
      margin="no"
      label="contract.document.documentSign.deleteSign"
      @click="openConfirmationModal"
    />

    <ModalConfirmation
      id="deleteConfirmationModal"
      ref="deleteConfirmationModal"
      message="contract.document.documentSign.deleteSign"
      title="contract.document.documentSign.deleteSignConfirm"
      button-confirm-title="contract.document.documentSign.deleteSign"
      button-cancel-title="main.bool.false"
      :loader="true"
      @confirm="deleteSign"
    />
  </div>
  <div class="row">
    <div class="col-12 col-lg-6">
      <BlockContent title-t="contract.document.documentSign.documents" title-level="5" class="pr-0">
        <template #header-right>
          <GenerateAndAddToSignButton
            v-if="!isDisabled"
            ref="generateAndAddToSignButton"
            :contract-id="contractId"
            :document-sign-id="documentSignId"
            @document-generated="handleSignersUpdated"
          />
        </template>
        <div v-if="documentSign" class="d-flex flex-column mt-3">
          <DocumentSignSignersForm
            ref="documentSignSignersForm"
            :document-sign="documentSign"
            :disabled="isDisabled"
            @signers-updated="handleSignersUpdated"
            @document-sign-file-replaced="handleSignFileReplaced"
            @document-sign-removed="handleSignersUpdated"
            @sign-cancelled="handleSignersUpdated"
          />
        </div>
        <div v-else>
          <PlaceholderContent :rows="3" />
        </div>
      </BlockContent>
    </div>

    <div class="col-12 col-lg-6">
      <BlockContent title-t="contract.document.documentSign.signers" title-level="5" class="pl-0">
        <DocumentSignerList
          v-if="documentSign"
          ref="documentSignerList"
          class="mt-3"
          :document-sign-id="documentSign.id"
          :contract-id="contractId"
          :disabled="isDocumentSignerListDisabled"
        />
        <PlaceholderContent v-else :rows="3" />
      </BlockContent>
    </div>

    <div class="col-12 col-lg-6">
      <DocumentSignFollowerList
        v-if="documentSign"
        ref="documentSignFollowerList"
        class="mt-3 pr-0"
        :document-sign="documentSign"
      />
    </div>
  </div>
</template>
